import { ReactNode, useEffect, useRef, useState } from 'react';

import {
  useUnifiedRfqsService,
  useRfqIdsList,
  useRfqsFetchData,
} from '@paradigm/features/src/unified-rfqs/contexts/unified-rfqs';
import { useWsStatus } from '@paradigm/features/src/unified-rfqs/contexts/use-ws-status';
import {
  hasConflictingProductSelection,
  useRfqFiltersValue,
} from '@paradigm/features/src/unified-rfqs/ui/rfq-filters/filter-provider';
import useResumeNotifications from '@paradigm/features/src/unified-rfqs/ui/one-click-trade/useResumeNotifications';
import FillScrollable from '#/components/FillScrollable';

import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { RfqHeaderRow } from '#/components/UnifiedRfqs/components/RfqHeaderRow';
import RfqsBottomLoader from '#/components/UnifiedRfqs/rfqs/RfqsBottomLoader';
import RfqListTable from '#/components/UnifiedRfqs/rfqs/RfqListTable';
import { TrackingCtxProvider } from '@paradigm/features/src/analytics';
import ObQuoteMatchingProvider from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-matching/ob-quote-matching-provider';
import useQuoteMatchFoundCallback from './useQuoteMatchFoundCallback';

export default function RfqTable() {
  const scrollableRef = useRef<HTMLDivElement>(null);
  const onQuoteMatchFound = useQuoteMatchFoundCallback();

  useResumeNotifications();
  const { fetcher } = useUnifiedRfqsService();
  const { status } = useRfqsFetchData();
  const [fetchError, setFetchError] = useState('');
  const rfqIds = useRfqIdsList();
  const { state: filterState } = useRfqFiltersValue();
  const { productCodes, assets, instruments, products, venues, strategyCodes } =
    filterState;

  const isFilterEmpty =
    assets.length + instruments.length + products.length + venues.length === 0;

  const isConflictingFilters = hasConflictingProductSelection(filterState);

  const { error: wsError, subscribed, subscribing, connected } = useWsStatus();

  useEffect(() => {
    if (!subscribed) return;
    setFetchError('');

    fetcher.fetchLatest({ productCodes, strategyCodes }, (failedResp) =>
      setFetchError(failedResp.message),
    );
    // Load latests once subscribed and when filters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productCodes, strategyCodes, subscribed]);

  const isEmpty = rfqIds.length === 0;
  const error = fetchError.length > 0 ? fetchError : wsError;
  const hasError = error.length > 0;
  const isInitialLoad =
    !connected || subscribing || status === 'fetching-latest';

  const content: ReactNode = isInitialLoad ? null : hasError ? (
    <pre css="color: red; margin-top: 1rem; text-align: center">{error}</pre>
  ) : isEmpty || isConflictingFilters ? (
    <p css="opacity: 0.7; margin-top: 1rem; text-align: center">
      {isFilterEmpty
        ? 'Use Create button to create a new RFQ or Order Book'
        : 'No Markets match selected filters'}
    </p>
  ) : (
    <RfqListTable rfqIds={rfqIds} scrollableRef={scrollableRef} />
  );
  const isMobile = useIsMobile();
  return (
    <TrackingCtxProvider value="markets table">
      <ObQuoteMatchingProvider onMatchFound={onQuoteMatchFound}>
        <FillScrollable
          ref={scrollableRef}
          id="unified-rfq-rfq-table"
          data-testid="rfq-table"
        >
          {!isMobile && <RfqHeaderRow />}
          {content}
          <RfqsBottomLoader scrollableRef={scrollableRef} />
        </FillScrollable>
      </ObQuoteMatchingProvider>
    </TrackingCtxProvider>
  );
}
